import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { formatarData } from "../lib/dados";
import {
  assinarImagens,
  caminhosDosBlocos,
  dataEHora,
  lerChamado,
  lerMeusChamados,
  tamanho,
  urlDoAnexoDaConsultoria,
  type BlocoDaConsultoria,
  type ChamadoDaLista,
  type ChamadoInteiro,
} from "../lib/consultoria";
import { carregarMeuPerfil } from "../lib/perfil";
import { Aviso, Carregando, Topo } from "../componentes/ui";
import TextoRico from "../componentes/TextoRico";
import Rodape from "../componentes/Rodape";

// Minhas consultorias — o HISTÓRICO dos chamados de consultoria do portal antigo (000351).
//
// Só leitura, por decisão do Enzo em 27/10: o aluno não reabre, não responde e não avalia de
// novo. A tela diz isso no alto, antes da lista, porque é o que decide o que a pessoa espera
// dela — um chamado "não encerrado" numa tela que parece de atendimento faria alguém esperar
// uma resposta que não vem.
//
// O atendente aparece pelo DEPARTAMENTO, e não pelo nome do consultor: o Bubble guarda o
// consultor de HOJE de cada departamento, e o chamado de 2024 pode ter sido atendido por outra
// pessoa. O nome errado ao lado de uma resposta é pior que nenhum nome.
//
// Uma rota só para as duas telas (a lista e o chamado), como o Leitor de Processos: o `:id`
// decide. O endereço do chamado viaja no link — recarregar ou voltar pelo navegador não perde
// onde a pessoa estava.
export default function Consultorias() {
  const { id } = useParams();
  const [eu, setEu] = useState<{ nome: string | null; avatar: string | null }>({ nome: null, avatar: null });

  useEffect(() => {
    let ativo = true;
    // O perfil é a mesma leitura do acervo: o `useHub` faria sete chamadas para pôr o nome no Topo.
    carregarMeuPerfil()
      .then((p) => {
        if (ativo) setEu({ nome: p?.nome ?? null, avatar: p?.avatar_url ?? null });
      })
      .catch(() => {});
    return () => {
      ativo = false;
    };
  }, []);

  return (
    <div className="min-h-screen">
      <Topo atual="inicio" nome={eu.nome} avatarUrl={eu.avatar} />
      <div className="mx-auto max-w-4xl px-4 pt-10 pb-16 sm:px-6">
        {id ? <TelaDoChamado id={id} /> : <ListaDeChamados />}
        <Rodape />
      </div>
    </div>
  );
}

function ListaDeChamados() {
  const [lista, setLista] = useState<ChamadoDaLista[] | null>(null);
  const [erro, setErro] = useState(false);

  useEffect(() => {
    let ativo = true;
    lerMeusChamados()
      .then((l) => {
        if (ativo) setLista(l);
      })
      .catch(() => {
        if (ativo) setErro(true);
      });
    return () => {
      ativo = false;
    };
  }, []);

  if (erro) {
    return (
      <Aviso titulo="Algo deu errado" acao={<Link to="/" className="btn-contorno">Voltar ao início</Link>}>
        Não conseguimos abrir as suas consultorias agora. Recarregue a página; se continuar, fale com o
        suporte.
      </Aviso>
    );
  }
  if (!lista) return <Carregando texto="Abrindo as suas consultorias…" />;

  return (
    <>
      <h1 className="font-display text-3xl font-bold text-titulo">Minhas consultorias</h1>
      <p className="mt-2 max-w-2xl text-cinza" data-aviso-historico>
        O histórico das consultorias que você fez com a nossa equipe no portal antigo. Elas ficam aqui
        para você consultar quando precisar; por esta tela não dá para responder nem abrir um
        atendimento novo.
      </p>

      {lista.length === 0 ? (
        <div className="surface mt-8 p-6">
          <h2 className="font-display text-lg font-bold text-titulo">Nenhuma consultoria por aqui</h2>
          <p className="mt-2 text-sm text-cinza">
            Não achamos consultoria feita com a sua conta no portal antigo.
          </p>
        </div>
      ) : (
        <ul className="mt-8 flex flex-col gap-3">
          {lista.map((c) => (
            <li key={c.id}>
              <Link
                to={`/consultorias/${c.id}`}
                data-chamado={c.id}
                className="surface porta block p-5"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-display text-lg font-bold text-titulo">{c.departamento}</span>
                  <span className="text-sm text-cinza">· {formatarData(c.criado_em)}</span>
                  <SeloDaSituacao encerrado={c.encerrado} respondido={c.respondido} />
                </div>
                {c.duvida && (
                  <p className="mt-2 line-clamp-2 text-sm text-tinta" data-duvida>
                    {c.duvida}
                  </p>
                )}
                <p className="mt-2 text-sm text-cinza">
                  {c.mensagens === 0
                    ? "Sem mensagens"
                    : c.mensagens === 1
                      ? "1 mensagem"
                      : `${c.mensagens} mensagens`}
                  {c.nota !== null && ` · você avaliou com ${c.nota} de 5`}
                </p>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

function SeloDaSituacao({ encerrado, respondido }: { encerrado: boolean; respondido: boolean }) {
  // A palavra vai junto da cor, sempre: o selo não depende de quem enxerga a cor.
  if (encerrado) {
    // `text-tinta`, e não `text-cinza`: o cinza sobre `bg-chip` dá 4,45 : 1 no tema claro (medido na prova).
    return <span className="rounded-full bg-chip px-2 py-0.5 text-xs font-semibold text-tinta">Encerrada</span>;
  }
  return (
    <span className="rounded-full bg-alerta-fundo px-2 py-0.5 text-xs font-semibold text-alerta-tinta">
      {respondido ? "Não foi encerrada" : "Ficou sem resposta"}
    </span>
  );
}

function TelaDoChamado({ id }: { id: string }) {
  const [estado, setEstado] = useState<"carregando" | "pronto" | "nao_encontrado" | "erro">("carregando");
  const [c, setC] = useState<ChamadoInteiro | null>(null);
  const [urls, setUrls] = useState<Map<string, string>>(new Map());

  useEffect(() => {
    let ativo = true;
    setEstado("carregando");
    (async () => {
      try {
        const r = await lerChamado(id);
        if (!ativo) return;
        if (!r) {
          setEstado("nao_encontrado");
          return;
        }
        setC(r);
        setEstado("pronto");
        const blocos = [
          ...r.duvida.flatMap((d) => d.blocos),
          ...r.triagem.flatMap((t) => t.blocos),
          ...r.mensagens.flatMap((m) => m.blocos),
        ];
        const assinadas = await assinarImagens(
          caminhosDosBlocos(blocos.filter((b) => b.tipo === "imagem")),
        );
        if (ativo) setUrls(assinadas);
      } catch {
        if (ativo) setEstado("erro");
      }
    })();
    return () => {
      ativo = false;
    };
  }, [id]);

  const voltar = (
    <Link to="/consultorias" className="text-sm font-semibold text-link hover:underline">
      ← Minhas consultorias
    </Link>
  );

  if (estado === "carregando") return <Carregando texto="Abrindo a consultoria…" />;
  if (estado === "erro") {
    return (
      <Aviso titulo="Algo deu errado" acao={<Link to="/consultorias" className="btn-contorno">Voltar</Link>}>
        Não conseguimos abrir esta consultoria agora. Recarregue a página; se continuar, fale com o suporte.
      </Aviso>
    );
  }
  if (estado === "nao_encontrado" || !c) {
    return (
      <Aviso titulo="Consultoria não encontrada" acao={<Link to="/consultorias" className="btn-contorno">Ver as minhas consultorias</Link>}>
        Não achamos esta consultoria na sua conta.
      </Aviso>
    );
  }

  const ch = c.chamado;
  const perfil = [ch.formacao, ch.profissao, ch.conhecimento && `nível ${ch.conhecimento.toLowerCase()}`].filter(Boolean);

  return (
    <>
      {voltar}
      <h1 className="mt-3 font-display text-3xl font-bold text-titulo">Consultoria · {ch.departamento}</h1>
      <p className="mt-1 flex flex-wrap items-center gap-2 text-sm text-cinza">
        Aberta em {formatarData(ch.criado_em)}
        <SeloDaSituacao encerrado={ch.encerrado} respondido={c.mensagens.some((m) => m.de_atendente && !m.automatica)} />
      </p>
      <p className="mt-2 max-w-2xl text-sm text-cinza">
        Esta consultoria é do portal antigo e fica aqui para consulta. Por esta tela não dá para
        responder.
      </p>

      {c.duvida.length > 0 && (
        <section aria-labelledby="duvida" className="surface mt-6 p-5">
          <h2 id="duvida" className="font-display text-lg font-bold text-titulo">Sua dúvida</h2>
          {c.duvida.map((d, i) => (
            <div key={i} className="mt-3 text-tinta" data-bloco-duvida>
              <Blocos blocos={d.blocos} urls={urls} />
            </div>
          ))}
        </section>
      )}

      {(c.triagem.length > 0 || perfil.length > 0) && (
        <details className="surface mt-4 p-5">
          {/* Fechada por padrão: é o que a pessoa respondeu para abrir o atendimento, e
              importa menos que a dúvida e a conversa. */}
          <summary className="cursor-pointer font-display font-bold text-titulo">
            O que você respondeu ao abrir
          </summary>
          {perfil.length > 0 && <p className="mt-3 text-sm text-cinza">Você se apresentou como: {perfil.join(" · ")}</p>}
          {c.triagem.map((t, i) => (
            <div key={i} className="mt-4" data-triagem>
              <p className="text-sm font-semibold text-titulo">{t.pergunta}</p>
              <div className="mt-1 text-sm text-tinta">
                <Blocos blocos={t.blocos} urls={urls} />
              </div>
            </div>
          ))}
        </details>
      )}

      <section aria-labelledby="conversa" className="mt-8">
        <h2 id="conversa" className="font-display text-xl font-bold text-titulo">A conversa</h2>
        {c.mensagens.length === 0 ? (
          <p className="mt-3 text-sm text-cinza">Esta consultoria não teve mensagens.</p>
        ) : (
          <ol className="mt-4 flex flex-col gap-3">
            {c.mensagens.map((m, i) => (
              <li
                key={i}
                data-mensagem={m.de_atendente ? "atendente" : "aluno"}
                data-automatica={m.automatica ? "" : undefined}
                // O mesmo cartão para os dois, e quem fala se lê pelo rótulo e pela posição: o
                // fundo `bg-chip` na mensagem do aluno deixava o rótulo cinza em 4,45 : 1 no tema
                // claro (medido na prova). A borda do atendente é o acento da casa.
                // A automática (000352) tem a borda neutra: ela não é a fala de um consultor.
                className={
                  m.automatica
                    ? "surface border-l-4 border-l-borda p-4"
                    : m.de_atendente
                      ? "surface border-l-4 border-l-acento p-4"
                      : "surface p-4 sm:ml-12"
                }
              >
                <p className="text-xs font-semibold text-cinza">
                  <span className="text-titulo">
                    {m.automatica ? "Mensagem automática" : m.de_atendente ? `Consultoria ${ch.departamento}` : "Você"}
                  </span>
                  {" · "}
                  {dataEHora(m.criado_em)}
                </p>
                <div className="mt-2 text-tinta">
                  <Blocos blocos={m.blocos} urls={urls} />
                </div>
              </li>
            ))}
          </ol>
        )}
      </section>

      {c.avaliacoes.length > 0 && (
        <section aria-labelledby="avaliacao" className="surface mt-8 p-5">
          <h2 id="avaliacao" className="font-display text-lg font-bold text-titulo">
            {c.avaliacoes.length === 1 ? "Sua avaliação" : "Suas avaliações"}
          </h2>
          {c.avaliacoes.map((a, i) => (
            <div key={i} className="mt-2" data-avaliacao>
              <p className="text-tinta">
                <span className="font-semibold text-titulo">{a.nota} de 5</span>
                <span className="text-sm text-cinza"> · {formatarData(a.criado_em)}</span>
              </p>
              {a.comentario && <p className="mt-1 text-sm text-tinta">{a.comentario}</p>}
            </div>
          ))}
        </section>
      )}

      <div className="mt-8">{voltar}</div>
    </>
  );
}

/**
 * Os blocos da conversa, no vocabulário da casa: nunca HTML. O texto passa pelo `TextoRico`, a
 * imagem sai pela URL assinada, e o código guarda o alinhamento (é um desenho: o fluxograma de
 * um consultor feito com espaços).
 */
function Blocos({ blocos, urls }: { blocos: BlocoDaConsultoria[]; urls: Map<string, string> }) {
  return (
    <>
      {blocos.map((b, i) => {
        if (b.tipo === "titulo") {
          return (
            <h3 key={i} className="mt-3 font-display font-semibold text-titulo first:mt-0">
              <TextoRico texto={b.texto} />
            </h3>
          );
        }
        if (b.tipo === "lista") {
          const Lista = b.ordenada ? "ol" : "ul";
          return (
            <Lista key={i} className={`mt-2 flex flex-col gap-1 pl-5 first:mt-0 ${b.ordenada ? "list-decimal" : "list-disc"}`}>
              {b.itens.map((it, j) => (
                <li key={j}>
                  <TextoRico texto={it} />
                </li>
              ))}
            </Lista>
          );
        }
        if (b.tipo === "codigo") {
          return (
            <pre key={i} className="mt-2 overflow-x-auto rounded-cf bg-chip p-3 font-mono text-sm text-tinta first:mt-0">
              {b.texto}
            </pre>
          );
        }
        if (b.tipo === "imagem") {
          const url = b.caminho ? urls.get(b.caminho) : undefined;
          if (url) {
            return (
              <img
                key={i}
                src={url}
                alt=""
                loading="lazy"
                data-imagem
                className="mt-2 max-w-full rounded-cf border border-borda first:mt-0"
              />
            );
          }
          return (
            <p key={i} className="mt-2 text-sm italic text-cinza first:mt-0" data-imagem-ausente>
              {b.perdida
                ? "Havia uma imagem aqui que não pôde ser trazida do portal antigo."
                : "Abrindo a imagem…"}
            </p>
          );
        }
        if (b.tipo === "arquivo") return <Anexo key={i} bloco={b} />;
        return (
          <p key={i} className="mt-2 first:mt-0">
            <TextoRico texto={b.texto} />
          </p>
        );
      })}
    </>
  );
}

function Anexo({ bloco }: { bloco: Extract<BlocoDaConsultoria, { tipo: "arquivo" }> }) {
  const [abrindo, setAbrindo] = useState(false);
  const [erro, setErro] = useState(false);
  if (!bloco.caminho || bloco.perdida) {
    return <p className="mt-2 text-sm italic text-cinza">Havia um anexo aqui que não pôde ser trazido do portal antigo.</p>;
  }
  const descricao = [bloco.mime === "application/pdf" ? "PDF" : bloco.mime?.includes("zip") ? "ZIP" : null, tamanho(bloco.bytes)]
    .filter(Boolean)
    .join(", ");
  return (
    <div className="mt-2 first:mt-0" data-anexo>
      <button
        type="button"
        disabled={abrindo}
        onClick={async () => {
          setAbrindo(true);
          setErro(false);
          const url = await urlDoAnexoDaConsultoria(bloco.caminho!, bloco.nome ?? "anexo");
          setAbrindo(false);
          if (url) window.location.assign(url);
          else setErro(true);
        }}
        className="btn-contorno text-sm disabled:opacity-60"
      >
        {abrindo ? "Abrindo…" : `Baixar o anexo${descricao ? ` (${descricao})` : ""}`}
      </button>
      {erro && <p className="mt-1 text-xs text-erro-tinta">Não abriu. Tente de novo.</p>}
    </div>
  );
}
